import { HeadlessAuthTransaction } from './headless-auth-flow.models';

/** Namespaced so it can never collide with the keycloak-js adapter's own storage. */
export const HEADLESS_AUTH_TRANSACTION_KEY = 'finxone.headless-auth-flow.transaction';
const TRANSACTION_KEY = HEADLESS_AUTH_TRANSACTION_KEY;

/** Keycloak login actions are far shorter lived than this; it is a hard ceiling, not a UX timer. */
const TRANSACTION_LIFETIME_MS = 10 * 60 * 1000;

const STATE_BYTES = 32;
const NONCE_BYTES = 32;
const CODE_VERIFIER_BYTES = 64;

/**
 * Mints a login transaction plus its S256 challenge. The verifier never leaves
 * sessionStorage and is consumed exactly once, at the authorization-code exchange.
 */
export async function createHeadlessAuthTransaction(input: {
  clientId: string;
  redirectUri: string;
}): Promise<{ transaction: HeadlessAuthTransaction; challenge: string }> {
  const transaction: HeadlessAuthTransaction = {
    state: randomBase64Url(STATE_BYTES),
    nonce: randomBase64Url(NONCE_BYTES),
    codeVerifier: randomBase64Url(CODE_VERIFIER_BYTES),
    createdAt: Date.now(),
    clientId: input.clientId,
    redirectUri: input.redirectUri,
  };
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(transaction.codeVerifier));
  return { transaction, challenge: toBase64Url(digest) };
}

export function saveHeadlessAuthTransaction(transaction: HeadlessAuthTransaction): void {
  sessionStorage.setItem(TRANSACTION_KEY, JSON.stringify(transaction));
}

/**
 * Reads, removes and validates the stored transaction. Removal happens before any
 * check so a rejected callback can never be replayed against the same verifier.
 */
export function consumeHeadlessAuthTransaction(expectedState: string): HeadlessAuthTransaction {
  const raw = sessionStorage.getItem(TRANSACTION_KEY);
  sessionStorage.removeItem(TRANSACTION_KEY);
  if (!raw) {
    throw new Error('The login transaction is missing. Start sign-in again.');
  }
  let transaction: HeadlessAuthTransaction;
  try {
    transaction = JSON.parse(raw) as HeadlessAuthTransaction;
  } catch {
    throw new Error('The login transaction was unreadable. Start sign-in again.');
  }
  if (!constantTimeEqual(transaction?.state ?? '', expectedState)) {
    throw new Error('The callback state did not match. Start sign-in again.');
  }
  if (Date.now() - transaction.createdAt > TRANSACTION_LIFETIME_MS) {
    throw new Error('The login transaction expired. Start sign-in again.');
  }
  return transaction;
}

export function clearHeadlessAuthTransaction(): void {
  sessionStorage.removeItem(TRANSACTION_KEY);
}

function randomBase64Url(bytes: number): string {
  return toBase64Url(crypto.getRandomValues(new Uint8Array(bytes)).buffer);
}

function toBase64Url(value: ArrayBuffer): string {
  let binary = '';
  for (const byte of new Uint8Array(value)) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** Compares the full length of both strings so a mismatch position leaks no timing signal. */
function constantTimeEqual(left: string, right: string): boolean {
  const length = Math.max(left.length, right.length);
  let result = left.length ^ right.length;
  for (let index = 0; index < length; index++) {
    result |= (left.charCodeAt(index) || 0) ^ (right.charCodeAt(index) || 0);
  }
  return result === 0;
}
